import { ProcessingLevel } from "@redotech/crdb-enums/fulfillment/processing-level";
import { ReceivingDisposition } from "@redotech/crdb-enums/fulfillment/receiving-disposition";
import { InventoryItemOutcome } from "@redotech/crdb-enums/inventory/inventory-item-outcome";
import { readReturnsCenterId } from "@redotech/redo-return-core-common/returns-center-id";
import * as z from "zod";
import {
  diffReturnsCenterValues,
  readReturnsCenterSettingPath,
  type ReturnsCenterValues,
} from "./returns-center-setting";

export const RECEIVING_OUTCOME_PATH = "redoManagedReturns.receivingOutcome";

/** Outcomes the receiving sort can give an item. */
export const RECEIVING_OUTCOMES = [
  InventoryItemOutcome.Consolidate,
  InventoryItemOutcome.Liquidate,
  InventoryItemOutcome.Destroy,
] as const;

export const receivingOutcomeSchema = z.enum(RECEIVING_OUTCOMES);

export type ReceivingOutcome = z.infer<typeof receivingOutcomeSchema>;

export const RECEIVING_OUTCOME_LABELS: Record<ReceivingOutcome, string> = {
  [InventoryItemOutcome.Consolidate]: "Consolidate",
  [InventoryItemOutcome.Liquidate]: "Liquidate",
  [InventoryItemOutcome.Destroy]: "Destroy",
};

/** The legacy disposition written beside an outcome; destroy has none. */
export function receivingDispositionForOutcome(
  outcome: ReceivingOutcome | null,
): ReceivingDisposition | null {
  switch (outcome) {
    case InventoryItemOutcome.Consolidate:
      return ReceivingDisposition.Consolidation;
    case InventoryItemOutcome.Liquidate:
      return ReceivingDisposition.Liquidation;
    case InventoryItemOutcome.Destroy:
    case null:
      return null;
  }
}

/** Receiving outcomes every listed returns center supports, in display order. */
export function receivingOutcomesSupportedByAll(
  supportedByCenter: readonly (readonly InventoryItemOutcome[])[],
): ReceivingOutcome[] {
  return RECEIVING_OUTCOMES.filter((outcome) =>
    supportedByCenter.every((supported) => supported.includes(outcome)),
  );
}

function byReturnsCenterId<T extends string>(
  values: ReturnsCenterValues<T>,
): Map<bigint, T | null> {
  return new Map(
    Object.entries(values).flatMap(([rawId, value]) => {
      const id = readReturnsCenterId(rawId);
      return id === null ? [] : [[id, value ?? null] as const];
    }),
  );
}

/**
 * Receiving centers whose outcome (their own, else the account-wide one) they don't support.
 * Centers at another level don't use the outcome, and unknown centers are skipped.
 */
export function centersWithUnsupportedReceivingOutcome(input: {
  accountLevel: ProcessingLevel;
  centerLevels: ReturnsCenterValues<ProcessingLevel>;
  accountOutcome: ReceivingOutcome | null;
  centerOutcomes: ReturnsCenterValues<ReceivingOutcome>;
  returnsCenterIds: readonly bigint[];
  supportedOutcomes: ReadonlyMap<bigint, readonly InventoryItemOutcome[]>;
}): bigint[] {
  const levels = byReturnsCenterId(input.centerLevels);
  const outcomes = byReturnsCenterId(input.centerOutcomes);
  return input.returnsCenterIds.filter((id) => {
    if ((levels.get(id) ?? input.accountLevel) !== ProcessingLevel.Receiving) {
      return false;
    }
    const outcome = outcomes.get(id) ?? input.accountOutcome;
    const supported = input.supportedOutcomes.get(id);
    return outcome != null && supported != null && !supported.includes(outcome);
  });
}

/**
 * Where the receiving level applies with no receiving outcome: the account-wide
 * level, and receiving centers with neither their own outcome nor the account's.
 */
export function receivingLevelsWithoutOutcome(input: {
  accountLevel: ProcessingLevel;
  centerLevels: ReturnsCenterValues<ProcessingLevel>;
  accountOutcome: ReceivingOutcome | null;
  centerOutcomes: ReturnsCenterValues<ReceivingOutcome>;
  returnsCenterIds: readonly bigint[];
}): { accountWide: boolean; returnsCenterIds: bigint[] } {
  const levels = byReturnsCenterId(input.centerLevels);
  const outcomes = byReturnsCenterId(input.centerOutcomes);
  return {
    accountWide:
      input.accountLevel === ProcessingLevel.Receiving &&
      input.accountOutcome == null,
    returnsCenterIds: input.returnsCenterIds.filter(
      (id) =>
        (levels.get(id) ?? input.accountLevel) === ProcessingLevel.Receiving &&
        (outcomes.get(id) ?? input.accountOutcome) == null,
    ),
  };
}

export function isReceivingOutcomePath(path: string): boolean {
  return path === RECEIVING_OUTCOME_PATH;
}

export function parseReceivingOutcomeValue(
  value: unknown,
): ReceivingOutcome | null {
  return value == null ? null : receivingOutcomeSchema.parse(value);
}

export function diffReceivingOutcome(
  stored: ReceivingOutcome | null,
  next: ReceivingOutcome | null | undefined,
): {
  path: string;
  prev: ReceivingOutcome | null;
  next: ReceivingOutcome | null;
}[] {
  if (next === undefined || stored === next) {
    return [];
  }
  return [{ path: RECEIVING_OUTCOME_PATH, prev: stored, next }];
}

export const RETURNS_CENTER_RECEIVING_OUTCOME_PATH =
  "redoManagedReturns.returnsCenterReceivingOutcome";

/** A center's override; null uses the account-wide outcome. */
export const returnsCenterReceivingOutcomesSchema = z.record(
  z.string(),
  receivingOutcomeSchema.nullable(),
);

export function readReturnsCenterReceivingOutcomePath(
  path: string,
): bigint | null {
  return readReturnsCenterSettingPath(
    RETURNS_CENTER_RECEIVING_OUTCOME_PATH,
    path,
  );
}

export function isReturnsCenterReceivingOutcomePath(path: string): boolean {
  return readReturnsCenterReceivingOutcomePath(path) !== null;
}

export function diffReturnsCenterReceivingOutcomes(
  stored: ReturnsCenterValues<ReceivingOutcome>,
  next: ReturnsCenterValues<ReceivingOutcome> | null | undefined,
): {
  path: string;
  prev: ReceivingOutcome | null;
  next: ReceivingOutcome | null;
}[] {
  return diffReturnsCenterValues(
    RETURNS_CENTER_RECEIVING_OUTCOME_PATH,
    stored,
    next,
  );
}
